import React, { useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AlertTriangle, Mail, MessageSquare } from "lucide-react";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    draft: true,
    intro: "This Privacy Policy explains what information sarkarijobai collects, how we use and protect it, and the choices you have. It is a template provided for transparency and must be reviewed by legal counsel before launch.",
    sections: [
      ["Information we collect", "Account details you provide (name, email, and — for Google sign-in — your Google profile name and picture). Your preparation profile (target exam, target score/rank, exam date, current level, daily study time). Performance inputs you add (mock scores, uploaded mock reports, practice results). Payment status (which plan, whether active) — we do not store full card numbers. Usage and device data (pages viewed, features used, approximate activity dates) to improve the product."],
      ["How we use your information", "To create and adapt your personalised daily study plan; to power the AI coach and mock-report analysis; to track your progress and consistency; to provide the Parent View summary; to process your subscription and eligibility for the Success Guarantee; to maintain security and improve the product. We do not sell your personal data."],
      ["AI processing", "To generate coaching responses and read uploaded mock reports, relevant inputs (such as your profile and the report you upload) are processed by third-party AI providers (e.g. Google Gemini via our integration partner). We send only what is needed for the feature and instruct providers not to use your data to train their models where such controls are available. AI outputs can be imperfect — always rely on official exam sources for authoritative information."],
      ["Cookies and sessions", "We use essential, httpOnly authentication cookies to keep you signed in. These are required for the app to function and are not used for advertising."],
      ["Data sharing", "We share data only with service providers who help us run the platform (hosting, database, AI processing, payments) under appropriate confidentiality obligations, or where required by law. We do not sell or rent your data."],
      ["Data retention", "We keep your data while your account is active and as needed to provide the service, resolve disputes, and meet legal obligations. You can request deletion at any time (see Your rights)."],
      ["Children and parents", "Some aspirants may be minors. Where a student is a minor, a parent or guardian should review and consent to the use of the platform. The optional Parent View shares a student's progress summary with the account holder. Contact us regarding any concern about a minor's data."],
      ["Data security", "We apply reasonable technical and organisational measures, including encrypted transport and hashed passwords, to protect your data. No method of transmission or storage is 100% secure, and we cannot guarantee absolute security."],
      ["Your rights", "Subject to applicable law, you may request access to, correction of, or deletion of your personal data, and you may object to or restrict certain processing. To exercise these rights, contact us using the details on the Contact page."],
      ["Changes to this policy", "We may update this policy from time to time. Material changes will be communicated through the app or by email, and the updated date below will change."],
      ["Contact", "Questions about privacy? Reach us via the Contact page (support@sarkarijobai.example). We will respond within a reasonable timeframe."],
    ],
  },
  terms: {
    title: "Terms of Service",
    draft: true,
    intro: "Placeholder terms of service. Final terms are subject to legal review before launch.",
    sections: [
      ["Use of service", "sarkarijobai provides AI-assisted exam preparation tools. You are responsible for your own study effort and for using official exam sources for authoritative information."],
      ["No guarantee of results", "sarkarijobai does not guarantee admission, rank, government employment or selection. Any refund guarantee applies only under the published eligibility and qualifying-outcome terms."],
      ["Subscriptions & billing", "Plans are billed per their stated terms. You may cancel according to your plan; cancellation stops future billing."],
      ["Acceptable use", "You agree not to misuse the platform, share access, or attempt to disrupt the service."],
    ],
  },
  "refund-policy": {
    title: "Refund Policy",
    draft: true,
    intro: "Placeholder refund policy. Final policy is subject to legal review.",
    sections: [
      ["Guarantee-based refunds", "Refunds under the Success Guarantee are available only where all published eligibility conditions and the defined qualifying outcome apply. See the Success Guarantee page."],
      ["Verification", "Eligible claims require verification of usage, activity and exam-attempt requirements."],
      ["Exclusions", "Requests outside the defined window, incomplete required activity, or terms violations may not qualify."],
    ],
  },
  "success-guarantee": {
    title: "The sarkarijobai Success Guarantee",
    draft: true,
    intro: "Follow the sarkarijobai preparation program, complete the required milestones, and if you meet the eligibility criteria but don't achieve the defined qualifying outcome, you may be eligible for a refund.",
    sections: [
      ["Eligibility", "May include completing the daily study time you chose at the start, a minimum subscription period, required study activity and consistency, required mock-test participation, required task completion, a genuine exam attempt, and other objective conditions in the final policy."],
      ["Qualifying outcome", "A specific, measurable result to be defined in the final published policy. This is not a promise of rank, selection, admission or government employment."],
      ["Refund process", "Eligible claims require verification; approved refunds are processed to the original payment method within a defined window."],
      ["Exclusions", "Not meeting minimum usage/activity, not attempting the exam, claims outside the window, or terms violations."],
      ["Disclaimer", "Final guarantee terms are subject to the published policy. Eligibility conditions apply and not every customer will qualify for a refund. sarkarijobai does not claim any fixed success rate."],
    ],
  },
};

export default function Legal({ type }) {
  const data = CONTENT[type];
  useEffect(() => {
    document.title = `${data?.title || "sarkarijobai"} — sarkarijobai`;
    window.scrollTo(0, 0);
  }, [data]);

  if (type === "contact") return <Contact />;
  if (type === "about") return <About />;
  if (!data) return null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 max-w-3xl">
        <h1 className="font-display text-4xl font-bold tracking-tight">{data.title}</h1>
        {data.draft && (
          <div className="mt-5 flex items-center gap-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs font-semibold text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4 shrink-0" /> DRAFT — FINAL TERMS SUBJECT TO LEGAL REVIEW
          </div>
        )}
        <p className="mt-6 text-base text-muted-foreground leading-relaxed">{data.intro}</p>
        <div className="mt-10 space-y-8">
          {data.sections.map(([h, b]) => (
            <div key={h}>
              <h2 className="font-display text-lg font-semibold mb-2">{h}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
        <p className="mt-12 text-xs text-muted-foreground border-t border-border pt-6">
          Last updated: placeholder. This content is illustrative and must be replaced by legally reviewed text before launch.
        </p>
      </main>
      <Footer />
    </div>
  );
}

function Contact() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Contact — sarkarijobai"; }, []);
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 max-w-3xl">
        <h1 className="font-display text-4xl font-bold tracking-tight">Contact us</h1>
        <p className="mt-6 text-muted-foreground">We're here to help with your preparation journey.</p>
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-border bg-card p-6">
            <Mail className="h-5 w-5 text-primary mb-3" />
            <p className="text-sm font-semibold">Email</p>
            <p className="text-sm text-muted-foreground mt-1">support@rankpilot.example</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <MessageSquare className="h-5 w-5 text-primary mb-3" />
            <p className="text-sm font-semibold">Support</p>
            <p className="text-sm text-muted-foreground mt-1">In-app support based on your plan tier.</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function About() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "About — sarkarijobai"; }, []);
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 max-w-3xl">
        <h1 className="font-display text-4xl font-bold tracking-tight">About sarkarijobai</h1>
        <p className="mt-6 text-base text-muted-foreground leading-relaxed">
          sarkarijobai is an AI exam success platform built for serious Indian competitive-exam aspirants.
          Most students don't lack content — they lack a system that tells them what deserves their time
          right now. sarkarijobai analyses your preparation, identifies what's costing you marks, builds your
          daily strategy, tracks your progress, and continuously adapts your preparation toward your target exam.
        </p>
        <p className="mt-4 text-base text-muted-foreground leading-relaxed">
          We can't sit your exam for you or control the paper. What we can do is build a rigorous system
          around your preparation — and put real skin in the game with an eligibility-based success guarantee.
        </p>
      </main>
      <Footer />
    </div>
  );
}
